import styled from "styled-components";
import { pxToRem } from "../../styles/utils";

export const AssignmentResponsePageContainer = styled.div`
  display: flex;
  height: calc(100% - ${pxToRem(24)});
  margin: ${pxToRem(12)};
`;

export const ContentWrapper = styled.div`
  min-width: 0;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  box-shadow: ${({ theme }) => theme.shadows.md}, ${({ theme }) => theme.shadows.ring};

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const PageInner = styled.div`
  width: 100%;
  max-width: ${pxToRem(1475)};
  box-sizing: border-box;
  padding: ${pxToRem(28)} ${pxToRem(34)} ${pxToRem(48)};

  @media (max-width: 760px) {
    padding: ${pxToRem(20)} ${pxToRem(16)} ${pxToRem(36)};
  }
`;

export const PageToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(16)};

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(7)};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.inkSoft};
  font: inherit;
  font-size: ${pxToRem(13)};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.brand};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(3)};
    border-radius: ${pxToRem(4)};
  }
`;

export const QueueNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};

  @media (max-width: 520px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;

export const QueueCount = styled.span`
  margin-right: ${pxToRem(4)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(12)};
  white-space: nowrap;
`;

export const NavigationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${pxToRem(34)};
  padding: ${pxToRem(7)} ${pxToRem(12)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${pxToRem(9)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.inkSoft};
  font: inherit;
  font-size: ${pxToRem(12)};
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSubtle};
    color: ${({ theme }) => theme.colors.brand};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${pxToRem(20)};
  margin: ${pxToRem(14)} 0 ${pxToRem(8)};
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${pxToRem(12)};
`;

export const PageTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(24)};
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.02em;
`;

export const StatusBadge = styled.span<{ $done: boolean }>`
  display: inline-flex;
  align-items: center;
  height: ${pxToRem(28)};
  padding: 0 ${pxToRem(11)};
  border-radius: ${pxToRem(8)};
  background: ${({ $done }) =>
    $done ? "rgba(86, 189, 116, 0.14)" : "rgba(176, 48, 76, 0.10)"};
  color: ${({ $done }) => ($done ? "rgb(61, 140, 86)" : "rgb(176, 48, 76)")};
  font-size: ${pxToRem(12)};
  font-weight: 500;
  white-space: nowrap;
`;

export const Crumbs = styled.div`
  margin-top: ${pxToRem(5)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(13)};

  span {
    margin: 0 ${pxToRem(7)};
    color: rgba(31, 10, 53, 0.24);
  }
`;

export const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${pxToRem(520)};
  gap: ${pxToRem(20)};
  align-items: stretch;
  margin-top: ${pxToRem(22)};

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
`;

export const MainColumn = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) minmax(0, 1fr);
  gap: ${pxToRem(14)};
  min-width: 0;
  height: 100%;

  @media (max-width: 1100px) {
    display: flex;
    flex-direction: column;
    height: auto;
  }
`;

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${pxToRem(14)};
  background: ${({ theme }) => theme.colors.white};
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(12)};
  flex: 0 0 auto;
  padding: ${pxToRem(14)} ${pxToRem(18)};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
`;

export const CardTitle = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(14)};
  font-weight: 500;
`;

export const CardCounter = styled.span`
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(12)};
`;

export const CardBody = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  padding: ${pxToRem(18)};
`;

export const ReviewPanelBody = styled(CardBody)`
  display: flex;
  flex-direction: column;
`;

export const StudentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(14)};
  padding: ${pxToRem(16)} ${pxToRem(18)};
`;

export const StudentName = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(15)};
  font-weight: 500;
`;

export const StudentMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${pxToRem(10)};
  margin-top: ${pxToRem(3)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(12)};

  a {
    color: ${({ theme }) => theme.colors.brand};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const StudentSide = styled.div`
  margin-left: auto;
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(12)};
  text-align: right;
  white-space: nowrap;

  @media (max-width: 620px) {
    margin-left: 0;
    white-space: normal;
  }
`;

export const AnswerText = styled.div<{ $empty?: boolean }>`
  color: ${({ $empty, theme }) => ($empty ? theme.colors.inkMuted : theme.colors.ink)};
  font-size: ${pxToRem(15)};
  line-height: 1.55;
  white-space: pre-wrap;
`;

export const AttachmentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${pxToRem(210)}, 1fr));
  gap: ${pxToRem(10)};
  margin-top: ${pxToRem(16)};
`;

export const AttachmentLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${pxToRem(10)};
  min-width: 0;
  padding: ${pxToRem(11)} ${pxToRem(12)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${pxToRem(10)};
  color: ${({ theme }) => theme.colors.ink};
  text-decoration: none;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSubtle};
  }
`;

export const AttachmentIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(34)};
  height: ${pxToRem(34)};
  flex: 0 0 auto;
  border-radius: ${pxToRem(8)};
  background: ${({ theme }) => theme.colors.inkSurface};
  color: ${({ theme }) => theme.colors.inkSoft};

  svg {
    width: ${pxToRem(18)};
    height: ${pxToRem(18)};
  }
`;

export const AttachmentName = styled.span`
  min-width: 0;
  overflow: hidden;
  color: inherit;
  font-size: ${pxToRem(13)};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TaskText = styled.div`
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(14)};
  line-height: 1.55;
  white-space: pre-wrap;
`;

export const TextAction = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.brand};
  font: inherit;
  font-size: ${pxToRem(13)};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const ReviewPanel = styled.div`
  display: flex;
  min-width: 0;
  align-self: stretch;

  > section {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 1100px) {
    display: block;

    > section {
      height: auto;
    }
  }
`;

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: ${pxToRem(7)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(13)};
`;

export const RatingGroup = styled.div`
  display: flex;
  gap: ${pxToRem(7)};
`;

export const RatingButton = styled.button<{ $selected: boolean }>`
  width: ${pxToRem(46)};
  height: ${pxToRem(42)};
  padding: 0;
  border: ${pxToRem(1)} solid
    ${({ $selected, theme }) => ($selected ? theme.colors.brand : theme.borders.outline)};
  border-radius: ${pxToRem(10)};
  background: ${({ $selected, theme }) => ($selected ? theme.colors.brand : "transparent")};
  color: ${({ $selected, theme }) => ($selected ? theme.colors.white : theme.colors.ink)};
  font: inherit;
  font-size: ${pxToRem(15)};
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    color: ${({ $selected, theme }) => ($selected ? theme.colors.white : theme.colors.brand)};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const RatingNote = styled.div`
  margin-top: ${pxToRem(7)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(12)};
  line-height: 1.4;
`;

export const ReviewField = styled.div`
  margin-top: ${pxToRem(18)};
`;

export const CommentInput = styled.textarea<{ $error: boolean }>`
  width: 100%;
  min-height: ${pxToRem(150)};
  box-sizing: border-box;
  resize: vertical;
  padding: ${pxToRem(12)} ${pxToRem(13)};
  border: ${pxToRem(1)} solid
    ${({ $error }) => ($error ? "rgb(176, 48, 76)" : "rgba(31, 10, 53, 0.26)")};
  border-radius: ${pxToRem(10)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  font: inherit;
  font-size: ${pxToRem(14)};
  line-height: 1.5;
  outline: none;

  &:hover {
    border-color: ${({ $error }) => ($error ? "rgb(176, 48, 76)" : "rgba(31, 10, 53, 0.38)")};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.brand};
    box-shadow: inset 0 0 0 ${pxToRem(1)} ${({ theme }) => theme.colors.brand};
  }
`;

export const Hint = styled.div<{ $error?: boolean }>`
  margin-top: ${pxToRem(6)};
  color: ${({ $error, theme }) => ($error ? "rgb(176, 48, 76)" : theme.colors.inkMuted)};
  font-size: ${pxToRem(12)};
  line-height: 1.4;
`;

export const Templates = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${pxToRem(7)};
  margin-top: ${pxToRem(11)};
`;

export const TemplateChip = styled.button`
  padding: ${pxToRem(6)} ${pxToRem(11)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${pxToRem(999)};
  background: transparent;
  color: ${({ theme }) => theme.colors.inkSoft};
  font: inherit;
  font-size: ${pxToRem(12)};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSubtle};
    color: ${({ theme }) => theme.colors.brand};
  }
`;

export const EditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${pxToRem(14)};
`;

export const ReviewActions = styled.div`
  display: grid;
  grid-template-columns: minmax(max-content, 1fr) minmax(max-content, 1fr);
  gap: ${pxToRem(10)};
  margin-top: auto;
  padding-top: ${pxToRem(18)};

  > button {
    width: 100%;
    min-width: max-content;
    max-width: none;
    padding-left: ${pxToRem(16)};
    padding-right: ${pxToRem(16)};
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;

    > button {
      min-width: 0;
    }
  }
`;

export const SentNote = styled.div`
  margin-top: ${pxToRem(10)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(12)};
  line-height: 1.4;
`;

export const DoneBox = styled.div`
  padding: ${pxToRem(15)} ${pxToRem(16)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  border-radius: ${pxToRem(10)};
  background: rgba(86, 189, 116, 0.06);
`;

export const DoneRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(10)};
  margin-bottom: ${pxToRem(9)};
`;

export const DoneRating = styled.div`
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(13)};

  strong {
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 600;
  }
`;

export const DoneText = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(14)};
  line-height: 1.5;
  white-space: pre-wrap;
`;

export const PageState = styled.div`
  padding: ${pxToRem(36)} 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(14)};
`;
